<template>
    <div>
        <div class="row mb-3">
            <div class="col-auto" v-if="journey">
                <navigate-to-actionable-report-link 
                    :journey-id="journey.id"
                    class="btn btn-outline-secondary"
                />
            </div>
            <div class="col-auto">
                <a class="btn btn-outline-secondary" href="/JourneyBuilder/Overview">
                    <i class="fas fa-route"></i> {{resource('JourneyBuilder_Index_Overview_Button_Text')}}
                </a>
            </div>
        </div>
        <bootstrap-box>
            <template #body>
                <template v-if="journey">
                    <div class="row">
                        <div class="col-3 menu-container">
                            <div class="menu-wrapper">
                                <!-- 
                                    Provide key as a slightly hacky workaround to force a full component reload on journey change
                                    this is the easiest way to get a newly created journey to show up in our journey selection dropdown 
                                -->
                                <menu-area
                                    :key="journey.id"
                                    
                                    :journey-id="journey.id"
                                    :journey-name.sync="journey.name"
                                    :journey-world.sync="journey.worldId"
                                    :journey-type.sync="journey.type"
                                    :journey-description.sync="journey.description"
                                    :journey-start-date.sync="journey.startDate"
                                    :journey-end-date.sync="journey.endDate"
                                    :journey-is-active.sync="journey.isActive"
                                    :journey-division-id.sync="journey.divisionId"

                                    v-on:update:journeyId="loadJourney($event)"
                                    v-on:update:journeyStartDate="setStartDate($event)"
                                    v-on:update:journeyEndDate="setEndDate($event)"

                                    @save="publishJourney"
                                    @clone="cloneJourney(journey.id)"
                                    @add-brick="addBrickToJourney"
                                >
                                </menu-area>
                            </div>
                        </div>

                        <div class="col">
                            <canvas-area
                                ref="composer"
                                
                                :journeyType="journey.type"

                                @top-left-changed="setNewBrickDefaultPosition($event.x, $event.y)"

                                @brick-moved="brickMoved($event.brickId, $event.x, $event.y)"
                                @connector-moved="connectorMoved($event.connectionId, $event.sourceBrickId, $event.connectorX, $event.connectorY)"

                                @add-brick-at-location="addBrickAtLocation"
                                @delete="deleteBrick($event.brick.id, $event.requiresPrompt)"

                                @add-connection="addConnectionToBrick($event.id)"
                                @settings-changed="updateSettings($event.id, $event.config, $event.name)"
                                @connection-created="connectBricks($event.connectionId, $event.sourceBrickId, $event.destinationBrickId)"
                                @change-connection="changeConnection($event.sourceBrickId, $event.connectionId, $event.model)"
                                @delete-connection-checkpoint="deleteCheckpoint($event.connection, $event.checkpoint)"
                                @delete-connection="deleteConnection($event.sourceBrickId, $event.destinationBrickId, $event.connectionId)"
                            >
                            </canvas-area>
                            <!--
                            TODO: handle event? 
                            @connection-active="setConnectorActive($event.connectionId, $event.sourceBrickId, $event.active)"
                            -->
                        </div>
                    </div>
                </template>
                <loading-box v-else />
            </template>
        </bootstrap-box>

        <delete-brick-prompt 
            v-if="brickDeletePrompt.show"
            :brick-id="brickDeletePrompt.brickId"
            :title="resource('JourneyBuilder_Index_DeleteBrick_ConfirmationTitle')"
            :body="resource('JourneyBuilder_Index_DeleteBrick_ConfirmationBody')"
            @yes="deleteBrick(brickDeletePrompt.brickId, false)"
            @hidden="brickDeletePrompt.show = false"
        />


        <deactivation-confirmation-popup
            @cancel="cancelDeactivation()"
            @save="confirmDeactivation($event)"

            v-if="displayDeactivationConfirmation"

            :object-id="journey.id"
            object-type="journey"
        />
    </div>
</template>

<script>
    import {createNamespacedHelpers} from 'vuex';
    import _ from 'lodash';
    
    import api from '@modules/JourneyBuilder/api';
    import createJourneyBrickFactory from '@modules/JourneyBuilder/Bricks/journey-brick-factory';
    import dateHelpers from '@modules/JourneyBuilder/Util/date-helpers';
    import confirmPublish from './confirm-publish';
    import { Checkpoint } from '@modules/JourneyBuilder/Bricks/Checkpoints';
    
    import {
        bootstrapDangerGrowl as errorGrowl,
        bootstrapSuccessGrowl as successGrowl
    } from "@core/Util/growler";

    import snowplow from '@modules/JourneyBuilder/snowplow';
    import { brickDefinitions } from '@modules/JourneyBuilder/Bricks/brick-definitions';
    import {promptV2 as prompt} from "@core/Util/prompt";
    import {getCurrentUser} from "@core/Util/current-user";

    import resourceMixin from "@core/Util/Mixins/resources";
    import cloneJourneyMixin from '@modules/JourneyBuilder/Mixins/clone-journey';
    import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
    
    import BootstrapBox from "@core/Components/Util/BootstrapBox.vue";
    import MenuArea from './MenuArea';
    import CanvasArea from "./CanvasArea";
    import { DeactivationConfirmationPopup } from "./Deactivation";
    import { ToggleButton } from 'vue-js-toggle-button';
    import DeleteBrickPrompt from "@modules/JourneyBuilder/Components/Composer/DeleteBrickPrompt";
    import LoadingBox from "@core/Components/Util/LoadingBox";
    import {NavigateToActionableReportLink} from "@modules/JourneyBuilder/Components/Elements";
    import { checkpointDefinitionsArray, checkpointDefinitions } from '@modules/JourneyBuilder/Bricks/Checkpoints';

    const {mapState, mapMutations, mapActions} = createNamespacedHelpers('journeyBuilder/composer');

    export default {
        name: 'ComposerIndex',
        
        components: {
            LoadingBox,
            BootstrapBox,
            MenuArea,
            CanvasArea,
            ToggleButton,
            DeleteBrickPrompt,
            NavigateToActionableReportLink,
            DeactivationConfirmationPopup
        },
        
        mixins: [resourceMixin, cloneJourneyMixin, permissionsMixin],

        provide(){
            const vm = this;

            return {
                providedJourney: {
                    get value(){
                        return vm.journey;
                    }
                }
            }
        },

        props: {
            id: {
                required: false,
                type: String
            }
        },
        
        data(){
            return {
                journey: null,
                // providedJourney: {
                //     value: null,
                // },
                //Object definition; overwritten in Create(...) handler
                journeyBrickFactory: {
                    createJourneyBrick: (type) => null,
                    createJourneyBrickFromModel: (remoteModel) => null
                },
                brickDefaultX: 0,
                brickDefaultY: 0,

                wasChangedAfterLastSave: false,

                brickDeletePrompt: {
                    show: false,
                    brickId: null
                },
                
                remoteJourneyIsActive: false,

                displayDeactivationConfirmation: false,
                cancelDeactivationCallback: () => {}
            }
        },
        
        computed: {
            ...mapState(['newComments', 'finishedInitialRenderCycle']),
            
            journeyIsActive() {
                if(!this.journey) {
                    return false;
                }
                
                const now = new Date();
                const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                return this.journey.isActive && (!this.journey.endDate || dateHelpers.getDateFromString(this.journey.endDate) > nowDate);
            }
        },

        watch: {
            'journey.id'(value){
                //Allow components to access our journey
                this.setJourneyId(value);
            },
            
            'journey.isActive'(newValue, oldValue){
                this.cancelDeactivationCallback = () => this.journey.isActive = oldValue;
            },
            
            'journey.endDate'(newValue, oldValue){
                this.cancelDeactivationCallback = () => this.journey.endDate = oldValue;
            },
            
            journeyIsActive(value) {
                //We don't show a popup when our remote journey is already inactive - keep our deactivation options in that case
                if(value && this.remoteJourneyIsActive) {
                    this.$delete(this.journey.config, 'deactivationOptions');
                }
                
                //We should only have to prompt upon a journey going from active -> inactive
                if(
                    value ||
                    !this.remoteJourneyIsActive
                ) { 
                    return;
                }
                
                this.displayDeactivationConfirmation = true;
            }
        },

        async created() {
            const loadConditionsProm = this.loadAvailableConnectionConditions();

            try {
                const brickDefs = await api.getJourneyBrickDefinitions();

                const typeConfigMapping = brickDefs.reduce((map, brickDef) => {
                    map[brickDef.type] = JSON.parse(brickDef.config);
                    return map;
                }, {});
                
                await loadConditionsProm;
                this.journeyBrickFactory = createJourneyBrickFactory(typeConfigMapping);

                await this.loadJourney(this.id);

                //We should only start watching our journey post-load
                //this to prevent accidentally triggering an 'altered' state on load
                this.$watch(
                    'journey',
                    () => this.wasChangedAfterLastSave = true,
                    { deep: true }
                );
            } catch (e) {
                errorGrowl(e);
            }
            
            window.addEventListener('beforeunload', evt => {
                if(this.canWrite && this.wasChangedAfterLastSave){
                    //According to the spec, preventDefault should be the go to way to prompt the user
                    //this is not implemented by all browsers yet however (Chrome)
                    //thus we'll also have to set the 'returnValue' property to ensure our handler
                    //works in all browsers
                    //the actual value of 'returnValue' is ignored by modern browsers
                    evt.preventDefault();
                    evt.returnValue = this.resource('JourneyBuilder_UnsavedChanges');
                }
            });
        },

        methods: {
            ...mapMutations(['addBrickToMap', 'updateBrickMapProperty', 'removeBrickFromMap', 'resetComments', 'setNewComment', 'setJourneyId']),

            ...mapActions(['setBrickMap', 'loadAvailableConnectionConditions']),

            ...mapActions({
                async loadBrickCommentIndicators(dispatch){
                    try {
                        await dispatch('loadBrickCommentIndicators', this.journey.id);
                    } catch(e) {
                        errorGrowl(e);
                    }
                }
            }),

            /**
             * Load the journey with the given id, a new empty journey will be loaded upon passing a journeyId of `undefined`
             * @param {String|undefined} journeyId
             * @returns {Promise<void>}
             */
            async loadJourney(journeyId) {
                if(
                    this.journey &&
                    this.journey.id === journeyId
                ) {
                    return;
                }
                
                if(this.wasChangedAfterLastSave) {
                    try {
                        await prompt('Van journey wisselen', 'Weet je zeker dat je van journey wilt wisselen? Al je niet opgeslagen wijzigingen zullen verloren gaan.');
                    } catch(e) {
                        return;
                    }
                }
                
                const isNewJourney = !journeyId;

                //Reset prior to updating journey to prevent accidentally triggering deactivated dialogue 
                this.remoteJourneyIsActive = false;
                
                //Start off by resetting our currently selected journey
                this.journey = undefined;
                
                //Display loader
                await this.$nextTick();
                
                //An empty journey provides a definition for our properties/ensures the UI does not crash
                let journeyAwaitable = new Promise((resolve) => resolve({
                    id: null,
                    name: '',
                    worldId: '',
                    type: 0,
                    description: '',
                    isActive: false,
                    bricks: [],
                    divisionId: getCurrentUser().DivisionId,
                    startDate: dateHelpers.getStringifiedDate(new Date()),
                    config: {}
                }));

                if(!isNewJourney) {
                    journeyAwaitable = api.getJourneyById(journeyId);
                }

                try {
                    this.journey = await journeyAwaitable;
                    this.reflectCurrentJourneyInBrowserUrl();

                    //We should normally not invoke mixin privates directly
                    this.$_setLockedState(true);

                    //Explicit brick creation gives us more flexibility regarding funcs/properties
                    //journey.bricks = brickModels.map(brickModel => vm.journeyBrickFactory.createJourneyBrickFromModel(brickModel));
                    const brickModels = this.journey.bricks;
                    const brickMap = {};
                    this.journey.bricks = brickModels.map(brickModel => this.journeyBrickFactory.createJourneyBrickFromModel(brickModel));
                    this.journey.bricks.forEach(brick => brickMap[brick.id] = brick);
                    this.setBrickMap(this.journey.bricks);

                    // Once all bricks have been created; we can start completing our connections
                    brickModels.forEach(brickModel => {
                        const brick = brickMap[brickModel.id];

                        brick.outgoingConnections.forEach(connection => {
                            const modelConnection = brickModel.connections.find(c => c.id === connection.id);
                            if(modelConnection && modelConnection.destinationBrickId){
                                connection.destinationBrick = brickMap[modelConnection.destinationBrickId];
                            }
                        });
                    });
                    
                    //Update the order utilized in our connections once the connections have been finalized
                    this.journey.bricks.forEach(
                        brick => brick.outgoingConnections.forEach(connection => Checkpoint.updateOrderForConnection(this.journey, connection))
                    );


                    //Wait for our watcher updating wasChangedAfterLastSave to run/allow our journeyIsActive computed to update
                    await this.$nextTick();

                    this.remoteJourneyIsActive = !isNewJourney && this.journeyIsActive;
                    this.wasChangedAfterLastSave = false;
                    
                    if(!isNewJourney){
                        await this.loadBrickCommentIndicators();
                    }
                } catch(err) {
                    errorGrowl(err);
                }
            },

            async publishJourney(){
                const vm = this;
                const isNewJourney = vm.journey.id === null;

                snowplow.trackStructuredEvent('publish-journey', vm.journey.id);

                try {
                    await confirmPublish(vm.journey);
                } catch(e){
                    //Cancelled
                    return;
                }

                //Ensure our deactivationOptions actually match the current state of the journey to prevent actually having a set of deactivationOptions for an active journey
                if(this.journeyIsActive) {
                    vm.$delete(vm.journey.config, 'deactivationOptions');
                }

                try {
                    //Make a deeplcone so we can safely remove all non-configured checkpoints
                    let journey = _.cloneDeep(vm.journey);

                    journey.bricks.forEach(brick => {
                        brick.outgoingConnections.forEach(connection => {
                            checkpointDefinitionsArray.forEach(checkpointDefinition => {
                                if(checkpointDefinition.isActiveForConnection(connection) && !checkpointDefinition.isConfiguredForConnection(connection)) {
                                    checkpointDefinition.deactivateForConnection(journey, connection);
                                }
                            })
                        });
                    });

                    journey = await api.publishJourney(journey, vm.newComments);

                    successGrowl(vm.resource('JourneyBuilder_Index_JourneyPublished_SuccessMessage'));

                    //Upon creation of a new journey; we want to reflect the newly created journey in our URL
                    //this makes for easier sharing of new journeys/no accidental loss of current journey upon page reload
                    if(isNewJourney){
                        //We only need the id of our newly created journey, everything else can remain as is
                        //this approach also ensures we don't lose any potential unconnected endpoints
                        vm.journey.id = journey.id;
                        vm.reflectCurrentJourneyInBrowserUrl();
                    }

                    //Upon saving; all bricks can be considered not-new, enabling a new set of features for the given brick
                    vm.journey.bricks.forEach(brick => {
                        vm.updateBrickMapProperty({ brickId: brick.id, property: 'isNew', value: false });
                        brick.outgoingConnections.forEach(connection => connection.isNew = false);
                    });

                    this.resetComments();
                    
                    //Our remote journey has changed, this change should also be reflected in our local state
                    vm.remoteJourneyIsActive = this.journeyIsActive;

                    //Disable confirmation popup on page leave
                    vm.wasChangedAfterLastSave = false;

                    //Include newly created comments in our brick comments
                    await this.loadBrickCommentIndicators();
                } catch(e){
                    errorGrowl(e);
                }
            },

            setStartDate(date){
                this.journey.startDate = date;
            },

            setEndDate(date){
                this.journey.endDate = date;
            },

            setNewBrickDefaultPosition(x, y){
                this.brickDefaultX = x;
                this.brickDefaultY = y;
            },

            addBrickAtLocation(info){
                const brickDefinition = info.brickDefinition;
                const location = info.location;

                const brick = this.addBrickToJourney(brickDefinition);
                brick.x = location.x;
                brick.y = location.y;
            },

            addBrickToJourney(brickDefinition){
                const vm = this;
                snowplow.trackStructuredEvent('add-brick', brickDefinition.type);

                const brick = vm.journeyBrickFactory.createNewJourneyBrick(brickDefinition.type);

                //Add 15px to ensure endpoint visibility
                brick.x = vm.brickDefaultX + 15;
                brick.y = vm.brickDefaultY + 15;

                vm.addBrickToMap(brick);
                this.journey.bricks.push(brick);

                return brick;
            },

            addConnectionToBrick(brickId){
                snowplow.trackStructuredEvent('add-connection', brickId);

                const brick = this.journey.bricks.find(b => b.id === brickId);
                brick.addConnection();
            },

            deleteBrick(brickId, requiresPrompt) {
                snowplow.trackStructuredEvent('delete-brick', brickId);

                const vm = this;

                if(requiresPrompt){
                    this.brickDeletePrompt.show = true;
                    this.brickDeletePrompt.brickId = brickId;
                    return;
                }

                const brick = vm.journey.bricks.find(b => b.id === brickId);

                //Create a copy as to not cause crashes/weird behavior due to the connections
                //being edited in the deleteConnection(...) function 
                [...brick.connections].forEach(connection => connection.delete());

                vm.journey.bricks.splice(vm.journey.bricks.indexOf(brick), 1);
                this.removeBrickFromMap(brick.id);
            },

            updateSettings(brickId, config, name) {
                const brick = this.journey.bricks.find(b => b.id === brickId);

                brick.config = config || {};
                brick.name = name || brickDefinitions[brick.type].name;
            },

            updateConnection(connectionId, config) {
                for(const brick of this.journey.bricks) {
                    let conn = brick.connections.find(c => c.id === connectionId);
                    if (conn) {
                        conn.config = Object.assign(conn.config, config.settings);
                    }
                }
            },

            connectBricks(connectionId, sourceBrickId, destinationBrickId){
                snowplow.trackStructuredEvent('connect-bricks', connectionId, JSON.stringify({sourceBrickId, destinationBrickId}));

                const sourceBrick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const destinationBrick = this.journey.bricks.find(b => b.id === destinationBrickId);

                const connection = sourceBrick.connections.find(r => r.id === connectionId);
                connection.destinationBrick = destinationBrick;
            },

            brickMoved(brickId, x, y){
                const brick = this.journey.bricks.find(b => b.id === brickId);

                brick.x = x;
                brick.y = y;
            },

            connectorMoved(connectionId, sourceBrickId, connectorX, connectorY){
                const brick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = brick.connections.find(c => c.id === connectionId);

                connection.connectorX = connectorX;
                connection.connectorY = connectorY;
            },

            changeStatus(connectionId, sourceBrickId, model) {
                const brick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = brick.connections.find(c => c.id === connectionId);

                connection.config = { ...connection.config, ...model };
            },

            changeConnection(sourceBrickId, connectionId, model) {
                snowplow.trackStructuredEvent('change-connection', model.id);

                const brick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = brick.connections.find(c => c.id === connectionId);
                connection.config = { ...connection.config, ...model };
            },

            deleteConnection(sourceBrickId, destinationBrickId, connectionId) {
                snowplow.trackStructuredEvent('delete-connection', connectionId, JSON.stringify({sourceBrickId, destinationBrickId}));

                const source = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = source.connections.find(c => c.id === connectionId);

                connection.delete();
            },

            deleteCheckpoint(connection, checkpoint) {
                const source = this.journey.bricks.find(b => b.id === connection.sourceBrickId);
                const conn = source.connections.find(c => c.id === connection.id);

                checkpointDefinitions[checkpoint].deactivateForConnection(this.journey, conn);
            },

            reflectCurrentJourneyInBrowserUrl(){
                const vm = this;

                if(window.history && window.history.replaceState){
                    window.history.replaceState(
                        null,
                        document.title,
                        api.getViewJourneyUrl(vm.journey.id)
                    )
                }
            },
            
            cancelDeactivation() {
                //Reset the property which caused our journey to become inactive
                this.cancelDeactivationCallback();
                this.displayDeactivationConfirmation = false;
            },

            confirmDeactivation(deactivationOptions) {
                this.$set(this.journey.config, 'deactivationOptions', deactivationOptions);
                this.displayDeactivationConfirmation = false;
            }
        }
    }
</script>

<style  scoped lang="scss">
	@import "~@modules/JourneyBuilder/Style/journey-builder-index.scss";
</style>
